import { SAMPLE_TAB } from '../../../core/tabs/sample';
import { toStageString } from '../../../core/tabs/types';
import { Box, Stack, Typography } from '@mui/material';
import type { ModuleTemplate } from '../../../core/types/module';
import {
  getBluesBoxHighlights,
  getDiatonicBoxHighlights,
  getPentatonicBoxHighlights,
  getTriadVoicingHighlights,
  resolveProgression,
} from '../../../utils/trainingModules';
import { noteToSemitone } from '../../../utils/musicTheory';

/** Previews use the same musical builders as the stage, never decorative random dots. */
export function LearnPreview({ module }: { module: ModuleTemplate }) {
  const {
    key,
    flavor = 'major',
    position = 1,
    chordQuality = 'major',
    triadGroupIndex = 0,
    inversion = 0,
  } = module.defaults;
  if (
    module.learnFamily === 'progressions' ||
    module.learnFamily === 'soloing'
  ) {
    const chords = resolveProgression(
      key,
      module.defaults.progressionId ?? 'i-iv-v-i'
    );
    return (
      <Stack justifyContent="center" spacing={1.5} sx={{ height: 132 }}>
        <Stack
          direction="row"
          spacing={0.75}
          aria-label={`Example progression in ${key}`}
        >
          {chords.map((chord, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 1.25,
                border: 1,
                borderColor: index === 0 ? 'primary.light' : 'divider',
                borderRadius: '6px',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {chord.numeral}
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {chord.root}
                {chord.quality === 'minor'
                  ? 'm'
                  : chord.quality === 'dominant'
                  ? '7'
                  : ''}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Example in {key} ·{' '}
          {module.learnFamily === 'soloing'
            ? 'Follow the chord tones'
            : 'Follow the changes'}
        </Typography>
      </Stack>
    );
  }
  const notes =
    module.learnFamily === 'tabs'
      ? SAMPLE_TAB.events
          .filter((event) => event.measure === 1 && event.type === 'note')
          .flatMap((event) =>
            event.notes.map((note) => ({
              string: toStageString(note.string),
              fret: note.fret,
            }))
          )
          .filter(
            (note, index, notes) =>
              notes.findIndex(
                (other) =>
                  other.string === note.string && other.fret === note.fret
              ) === index
          )
      : module.learnFamily === 'triads'
      ? getTriadVoicingHighlights(key, chordQuality, triadGroupIndex, inversion)
      : module.learnFamily === 'blues'
      ? getBluesBoxHighlights(key, position - 1, flavor)
      : module.learnFamily === 'diatonic'
      ? getDiatonicBoxHighlights(key, position, flavor)
      : getPentatonicBoxHighlights(key, flavor, position - 1);
  const start = Math.max(0, Math.min(...notes.map((note) => note.fret)) - 1);
  const frets = Math.max(
    4,
    Math.max(...notes.map((note) => note.fret)) - start
  );
  const fretWidth = 256 / frets;
  const root = noteToSemitone(key);
  const openStrings = [4, 9, 2, 7, 11, 4];
  const example =
    module.learnFamily === 'tabs'
      ? 'Example fret locations from an original JamCam exercise.'
      : `Example: ${key} ${
          module.learnFamily === 'triads'
            ? chordQuality + ' triad'
            : flavor + ', position ' + position
        }. Filled dots mark roots.`;
  return (
    <Box sx={{ color: 'primary.light', width: '100%', height: 132 }}>
      <svg
        viewBox="0 0 320 132"
        width="100%"
        height="100%"
        role="img"
        aria-label={example}
      >
        {Array.from({ length: frets + 1 }, (_, index) => (
          <line
            key={`fret-${index}`}
            x1={44 + index * fretWidth}
            x2={44 + index * fretWidth}
            y1="18"
            y2="103"
            stroke="currentColor"
            strokeOpacity="0.2"
          />
        ))}
        {['e', 'B', 'G', 'D', 'A', 'E'].map((label, index) => (
          <g key={label}>
            <text
              x="14"
              y={22 + index * 17}
              fill="currentColor"
              opacity="0.65"
              fontSize="10"
            >
              {label}
            </text>
            <line
              x1="28"
              x2="300"
              y1={18 + index * 17}
              y2={18 + index * 17}
              stroke="currentColor"
              strokeOpacity="0.25"
            />
          </g>
        ))}
        {Array.from({ length: frets }, (_, index) => (
          <text
            key={index}
            x={44 + (index + 0.5) * fretWidth}
            y="125"
            textAnchor="middle"
            fill="currentColor"
            opacity="0.65"
            fontSize="10"
          >
            {start + index + 1}
          </text>
        ))}
        {notes.map((note) => {
          const isRoot =
            module.learnFamily === 'tabs'
              ? note.string === notes[0]?.string && note.fret === notes[0]?.fret
              : (openStrings[note.string] + note.fret) % 12 === root;
          return (
            <circle
              key={`${note.string}-${note.fret}`}
              cx={
                note.fret === 0
                  ? 32
                  : 44 + (note.fret - start - 0.5) * fretWidth
              }
              cy={18 + (5 - note.string) * 17}
              r={isRoot ? 6 : 4.5}
              fill={
                isRoot
                  ? 'currentColor'
                  : 'var(--learn-preview-surface, #121923)'
              }
              stroke="currentColor"
              strokeWidth={isRoot ? 1.5 : 1}
            />
          );
        })}
      </svg>
    </Box>
  );
}
